import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatSAR, formatIDR, statusColor, waLink, buildWaMessage } from "@/lib/utils";
import { toast } from "sonner";
import { MessageCircle, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

function TripRow({ tr, t }) {
  const time = tr.pickup_date?.slice(11, 16) || "--:--";
  return (
    <div className="flex items-center gap-3 py-2 border-b last:border-0" data-testid={`sched-${tr.code}-${tr.trip_index}`}>
      <div className="font-mono font-bold text-emerald-700 w-14">{time}</div>
      <div className="flex-1 min-w-0"><div className="font-medium truncate">{tr.route}</div>
        <div className="text-xs text-slate-500 truncate">{tr.customer_name} · {tr.vehicle_type} · {tr.passengers} pax{tr.pickup_location ? ` · ${tr.pickup_location}` : ""}</div></div>
      <Badge className={statusColor(tr.payment_status)}>{t(`pay_${tr.payment_status}`)}</Badge>
      <a href={waLink(tr.customer_phone, `Assalamu'alaikum ${tr.customer_name}, pengingat perjalanan ${tr.route} (${tr.code}) pada ${tr.pickup_date.replace("T", " ")}. Driver kami akan siap di lokasi. - Abdan Pro`)} target="_blank" rel="noreferrer">
        <Button size="sm" variant="ghost" data-testid={`sched-wa-${tr.code}`}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button></a>
    </div>
  );
}

export default function TodaySchedule() {
  const { t } = useApp();
  const [d, setD] = useState(null);
  const load = () => api.get("/reports/today").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const confirmDp = async (b) => {
    try { await api.patch(`/bookings/${b.id}/payment`, { payment_status: "dp_paid" }); toast.success(t("saved")); load(); } catch { toast.error(t("failed")); }
  };
  if (!d) return null;
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      {[["today", t("today")], ["tomorrow", t("tomorrow")]].map(([k, label]) => (
        <Card key={k} data-testid={`schedule-${k}`}><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-emerald-600" />{label} <span className="text-slate-400 font-normal">({d[k].length})</span></CardTitle></CardHeader>
          <CardContent>{d[k].length === 0 ? <div className="text-sm text-slate-500 py-3">{t("no_trips")}</div> : d[k].map((tr, i) => <TripRow key={i} tr={tr} t={t} />)}</CardContent></Card>
      ))}
      <Card data-testid="unpaid-dp"><CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" />{t("unpaid_dp")} <span className="text-slate-400 font-normal">({d.unpaid_dp.length})</span></CardTitle></CardHeader>
        <CardContent>
          {d.unpaid_dp.length === 0 ? <div className="text-sm text-slate-500 py-3">{t("all_dp_paid")}</div> : d.unpaid_dp.map((b) => (
            <div key={b.id} className="flex items-center gap-2 py-2 border-b last:border-0" data-testid={`unpaid-${b.code}`}>
              <div className="flex-1 min-w-0"><div className="font-mono font-bold text-sm">{b.code} {b.payment_proof && <Badge className="ml-1 bg-sky-100 text-sky-800 border-sky-300">{t("proof_received")}</Badge>}</div>
                <div className="text-xs text-slate-500 truncate">{b.customer_name} · {b.pickup_date?.slice(0, 10)} · DP {formatIDR(b.dp_amount_idr)} ({formatSAR(b.dp_amount)})</div></div>
              <a href={waLink(b.customer_phone, buildWaMessage(b, "reminder"))} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" data-testid={`unpaid-wa-${b.code}`}><MessageCircle className="w-4 h-4 text-emerald-600" /></Button></a>
              <Button size="sm" variant="outline" onClick={() => confirmDp(b)} data-testid={`unpaid-confirm-${b.code}`}><CheckCircle2 className="w-4 h-4 mr-1" />DP</Button>
            </div>))}
        </CardContent></Card>
    </div>
  );
}
